import angular from 'angular';

/*******************************************************************************
 * runDataGridReceiptItem */
/**
 * Datagrid receipt item class. This will manage a source ReceiptItem object.
 *******************************************************************************/
/* @ngInject */ export function runDataGridReceiptItem(
  Data,
  $filter,
  $sce,
  SERVER,
  Tools,
  DistributionSpec
) {
  /** Current negative numeric temporary ID. This is used for new source
    ReceiptItems in case of item ID referencing. */
  var mTempID = 0;

  /*****************************************************************************
   * Class: DGReceiptItem
   *
   * Datagrid receipt item class. This will manage a source ReceiptItem object.
   *
   * @param src     ReceiptItem source to manage
   * @param isChild Child/Kit item indicator
   *****************************************************************************/
  Data.DGReceiptItem = function (src, isChild) {
    /***************************************************************************
     * Constants
     ***************************************************************************/
    var DT_MEMBERSHIP = SERVER.BooksDBConstants.DT_MEMBERSHIP;
    var DT_STUDENT = SERVER.BooksDBConstants.DT_STUDENT;
    var DT_TEACHER = SERVER.BooksDBConstants.DT_TEACHER;

    var ET_STUDENT = SERVER.BooksDBConstants.ET_STUDENT;
    var ET_TEACHER = SERVER.BooksDBConstants.ET_TEACHER;

    var IT_MEMBERSHIP = SERVER.BooksDBConstants.IT_MEMBERSHIP;

    var LIS_ITEM_GROUP = SERVER.BooksDBConstants.LIS_ITEM_GROUP;
    var LIS_MEMBERSHIP = SERVER.BooksDBConstants.LIS_MEMBERSHIP;
    var LIS_DESTINY_FINE_PAYMENT =
      SERVER.BooksDBConstants.LIS_DESTINY_FINE_PAYMENT;

    var LIT_DONATION_PURCHASE = SERVER.BooksDBConstants.LIT_DONATION_PURCHASE;
    var LIT_DONATION_RETURN = SERVER.BooksDBConstants.LIT_DONATION_RETURN;
    var LIT_FUNDRAISER_CREDIT = SERVER.BooksDBConstants.LIT_FUNDRAISER_CREDIT;
    var LIT_ITEM_ORDER = SERVER.BooksDBConstants.LIT_ITEM_ORDER;
    var LIT_ITEM_PICKUP = SERVER.BooksDBConstants.LIT_ITEM_PICKUP;
    var LIT_ITEM_RETURN = SERVER.BooksDBConstants.LIT_ITEM_RETURN;
    var LIT_ITEM_SALE = SERVER.BooksDBConstants.LIT_ITEM_SALE;
    var LIT_JOURNAL_CREDIT = SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT;
    var LIT_RECEIVABLE_CREDIT = SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT;
    var LIT_SERVICE_FEE_CREDIT = SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT;
    //Add type for Destiny Fine.

    /***************************************************************************
     * Properties
     ***************************************************************************/
    /** Instance Reference. */ var mThis = this;
    /** ReceiptItem source. */ var mSource = src;

    /** Is child item. */ var mIsChild = !!isChild;
    /** Product List Item Source */ mThis.productSource = null;

    /*--------------------*/
    /* Caching Properties */
    /*--------------------*/
    /** Cached LedgerItemOptions list. */ var mCachedOptions;
    /** Cached Html Tooltip content. */ var mCachedHtmlContent;

    /***************************************************************************
     * Property Getter/Setters for grid cell rendering/updating
     ***************************************************************************/
    Object.defineProperty(this, 'memo', {
      get: function () {
        return mSource.memo;
      },
      set: function (value) {
        if (mThis.isPayment()) mSource.memo = value;
      }
    });

    Object.defineProperty(this, 'amount', {
      get: function () {
        return mSource.amount;
      },
      set: function (value) {
        if (mThis.isAmountOnly()) mSource.amount = Number(value);
      }
    });

    Object.defineProperty(this, 'returnedAmount', {
      get: function () {
        if (mThis.isGroup()) {
          return mThis.subItems.reduce(
            (groupAmount, subItem) => (groupAmount += subItem.amount),
            0
          );
        }

        return mSource.returnedAmount;
      }
    });

    Object.defineProperty(this, 'quantity', {
      get: function () {
        return displayQuantity() ? mSource.quantity : NaN;
      },
      set: function (value) {
        if (mThis.isAmountOnly() || !mThis.allowEditQty()) return;

        /*---------------------------*/
        /* Ignore non-numeric values */
        /*---------------------------*/
        if (isNaN((value = Number(value)))) return;

        /*---------------------------------------------------------------*/
        /* Make sure the value is an integer, and has no decimal values. */
        /*---------------------------------------------------------------*/
        value = Math.trunc(value);

        /*------------------------------------------*/
        /* Update the discount for the new quantity */
        /*------------------------------------------*/
        mSource.discount = mSource.quantity
          ? Tools.roundToPrecision(
              ((mSource.discount || 0) / mSource.quantity) * value,
              2
            )
          : 0;
        mSource.quantity = value;

        if (mThis.isOrder()) mSource.backordered = value;

        /*-----------------------------------------*/
        /* Update childItems with the new quantity */
        /*-----------------------------------------*/
        mThis.childDGItems().forEach(function (ci) {
          ci.quantity = value;
        });

        recalc();
      }
    });

    Object.defineProperty(this, 'price', {
      get: function () {
        return displayPrice() ? mSource.price : NaN;
      },
      set: function (value) {
        if (!mThis.allowEditPrice()) return;

        mSource.price = Tools.roundToPrecision(value + priceChange(), 2);

        recalc();
      }
    });

    /** Html Tooltip text for options. This is to be used with the uibTooltipHtml directive.
      This accessor will manage its own caching of values so an infinite digest cycle
      error will be prevented. This has to be done this way as there is no scope
      available at this level. */
    Object.defineProperty(this, 'tooltipHtml', {
      get: function () {
        if (!angular.equals(mCachedOptions, mThis.options())) {
          /*----------------------------------*/
          /* A new array reference is used to */
          /* element equality can be checked. */
          /*----------------------------------*/
          mCachedOptions = mThis.options().concat();

          if (mCachedOptions.length)
            mCachedHtmlContent = $sce.trustAsHtml(
              mThis
                .options()
                .map(function (lio) {
                  return '<b>{0}</b>: {1}'.format(
                    lio.optionMasterName,
                    lio.optionValue
                  );
                })
                .join('<br/>')
            );
          else mCachedHtmlContent = null;
        }

        return mCachedHtmlContent;
      }
    });

    /***************************************************************************
     * Getter Functions
     ***************************************************************************/
    /** Ensures an array reference. */
    mThis.childDGItems = function () {
      return mThis.subItems || [];
    };
    mThis.options = function () {
      return mSource.options || [];
    };

    /** ID */ mThis.id = function () {
      return mSource.id;
    };
    /** Source ReceiptItem. */ mThis.source = function () {
      return mSource;
    };
    /** Item ID. */ mThis.itemID = function () {
      return mSource.itemID || 0;
    };
    /** Tax Amount. */ mThis.tax = function () {
      return mThis.isAmountOnly() ? NaN : mSource.tax;
    };
    /**
     * Returns the original unit price the user entered before any discount was applied.
     * The grid item may already have a discount applied while the new item being compared does not,
     * so we recover the pre-discount price by adding back the per-unit discount amount.
     */
    mThis.getOriginalUnitPrice = function () {
      return Tools.roundToPrecision(
        mSource.price + (mSource.discount || 0) / (mSource.quantity || 1),
        2
      );
    };

    /*****************************************************************************
     * Description */
    /**
     * Returns string to be displayed in the UI-Grid Memo column in WriteReceipt
     *****************************************************************************/
    mThis.description = function () {
      if (mThis.isServiceFee()) return 'Convenience Fee';

      /*-----------------------------*/
      /* Build description from memo */
      /*-----------------------------*/
      var baseDescription = mSource.memo || '';
      var additionalInfo = !mIsChild ? mThis.additionalDescription() : '';
      var orderIndicator = mThis.isOrder() ? '*' : '';

      /*------------------------------------*/
      /* Return the constructed description */
      /*------------------------------------*/
      return (
        baseDescription +
        additionalInfo +
        mThis.descriptionEffectiveDate() +
        orderIndicator
      );
    };

    /***************************************************************************
     * descriptionEffectiveDate */
    /**
     * Returns the current effective date for membership items meant to be
     * appended after the existing description. Display only, this is not part
     * of the saved memo. Applied to child items too since a kit's membership
     * is one of its subitems.
     *
     * Membership is checked against the product as well as the subType. The
     * server only reports LIS_MEMBERSHIP when the item's product is loaded,
     * which it is not for a saved receipt, so loaded items come back as 0.
     ***************************************************************************/
    mThis.descriptionEffectiveDate = function () {
      var isMembershipLine =
        mSource.subType == LIS_MEMBERSHIP ||
        (mThis.productSource && mThis.productSource.type == IT_MEMBERSHIP);

      return isMembershipLine && mSource.effectiveDate
        ? ' -- Effective: ' +
            $filter('date')(mSource.effectiveDate, 'MM/dd/yyyy')
        : '';
    };

    /***************************************************************************
     * descriptionClass */
    /**
     * Returns a string with the associated class name if available meant to be
     * appended after the existing description.
     ***************************************************************************/
    mThis.descriptionClass = function () {
      return mSource.classID && mSource.className
        ? ' - ' + mSource.className
        : '';
    };

    /***************************************************************************
     * descriptionName */
    /**
     * Returns a string with who the item is for when the name field is set.
     ***************************************************************************/
    mThis.descriptionName = function () {
      return mSource.name ? ' \nFor: ' + mSource.name : '';
    };

    /***************************************************************************
     * Flag functions
     ***************************************************************************/
    /*------------------*/
    /* Allow edit flags */
    /*------------------*/
    function allowEdit() {
      return (
        mThis.isOther() ||
        (!mThis.isServiceFee() &&
          !mThis.isPayment() &&
          (!mThis.isMembership() || mThis.isGroup()))
      );
    }

    mThis.allowEditAmount = function () {
      return mThis.isPayment();
    };
    mThis.allowEditMemo = function () {
      return mThis.isPayment();
    };
    mThis.allowEditPrice = function () {
      return allowEdit() && !mThis.isAmountOnly() && !mThis.isFixedPrice();
    };
    mThis.allowEditQty = function () {
      return (
        allowEdit() &&
        !mIsChild &&
        mThis.isSaleOrder() &&
        !mThis.isMembership() &&
        !mThis.hasQtyChange()
      );
    };

    /** This item can be removed if its not a child item and its new or in the case of an ItemSale or any subclass,
      that there have been no returns or pickups against the item. Payments/credits made against it on other
      receipts lock the whole receipt instead (rcpt.credited -> unvoidable -> enableItems), and the server
      refuses changing or removing a credited item (Receipt.cs's edit path). */
    mThis.allowRemove = function () {
      return (
        !mIsChild &&
        (mThis.isNew() ||
          !mThis.isSaleOrder() ||
          (!mThis.hasReturns() && !mThis.hasPickups()))
      );
    };

    /*-----------------*/
    /* Indicator flags */
    /*-----------------*/
    function displayQuantity() {
      return !mThis.isChildItem() && displayPrice();
    }
    function displayPrice() {
      return !mThis.isAmountOnly();
    }

    mThis.hasQtyChange = function () {
      return (
        (mThis.isGroup() &&
          mThis.childDGItems().some(function (si) {
            return si.hasQtyChange();
          })) ||
        mThis.options().some(function (option) {
          return option.qtyChange > 0;
        })
      );
    };

    /** Returns an indication if any of the qty sold have been returned or in the case of a group if
      any sub items have been returned. Also covers the four flat-amount
      return types (donation, student funds purchase, service fee,
      receivable payment) - GR-2375 - which have no quantity concept, so a
      return/reversal against them shows up as mSource.returnedAmount > 0
      instead (see DonationPurchase.returnedAmount et al. server-side for
      where that's populated). Previously this only ever checked the
      quantity-based item-sale/order case, so a receipt whose only return was
      e.g. a donation return or a receivable refund silently never registered
      as having returns - see WriteReceipts.js's mUI_Flags.hasReturns, which
      this feeds, and allowRemove()/hideDelete() below. */
    mThis.hasReturns = function () {
      if (mThis.isGroup())
        return mThis.childDGItems().some(function (dgi) {
          return dgi.hasReturns();
        });

      if (mThis.isSaleOrder()) {
        /** Utility function to return total qty sold. */
        var totalQty = function () {
          var qtyChange = 0;
          mThis.options().forEach(function (lio) {
            qtyChange += lio.qtyChange;
          });

          return mSource.quantity + qtyChange;
        };

        return !mThis.isNew() && mSource.qtyOrdered != totalQty();
      }

      return (
        !mThis.isNew() &&
        (mThis.isDonation() ||
          mThis.isStudentFundsItem() ||
          mThis.isServiceFee() ||
          mThis.isPayment()) &&
        (mSource.returnedAmount || 0) > 0
      );
    };

    mThis.hasOptions = function () {
      return !!mThis.options().length;
    };
    mThis.hasPickups = function () {
      return mThis.isOrder() && mSource.qtyDelivered != 0;
    };
    mThis.hideDelete = function () {
      return (
        mThis.isChildItem() ||
        mSource.type == LIT_ITEM_RETURN ||
        mThis.hasReturns() ||
        mThis.hasPickups()
      );
    };
    mThis.inputEditOnly = function () {
      return (
        (!mThis.isChildItem() && mThis.isSaleOrder()) ||
        mThis.isOther() ||
        mThis.isDonation()
      );
    };
    mThis.isAmountOnly = function () {
      return (
        mThis.isDonation() ||
        mThis.isOther() ||
        mThis.isPayment() ||
        mThis.isPickup() ||
        mThis.isServiceFee() ||
        mThis.isStudentFundsItem()
      );
    };
    mThis.isChildItem = function () {
      return mIsChild;
    };
    mThis.isDonation = function () {
      return (
        mSource.type == LIT_DONATION_PURCHASE ||
        mSource.type == LIT_DONATION_RETURN
      );
    };
    mThis.isStudentFundsItem = function () {
      return (
        mSource.type == SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_PURCHASE ||
        mSource.type == SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_RETURN
      );
    };
    mThis.isFixedPrice = function () {
      return mThis.productSource && mThis.fixedPrice;
    };
    mThis.isGroup = function () {
      return mSource.subType == LIS_ITEM_GROUP;
    };
    mThis.isDestinyPayment = function () {
      return mSource.subType == LIS_DESTINY_FINE_PAYMENT;
    };

    /** This item is for a membership or this is a kit which contains a membership item. */
    mThis.isMembership = function () {
      return (
        mSource.subType == LIS_MEMBERSHIP ||
        mThis.childDGItems().some(function (si) {
          return si.isMembership();
        })
      );
    };

    mThis.isNew = function () {
      return mSource.id <= 0;
    };
    mThis.isOrder = function () {
      return mSource.type == LIT_ITEM_ORDER;
    };
    mThis.isOther = function () {
      return (
        mSource.type == LIT_FUNDRAISER_CREDIT ||
        mSource.type == LIT_JOURNAL_CREDIT
      );
    };
    mThis.isPayment = function () {
      return mSource.type == LIT_RECEIVABLE_CREDIT;
    };
    mThis.isReturned = function () {
      return mSource.isReturned;
    };
    mThis.isServiceFee = function () {
      return mSource.type == LIT_SERVICE_FEE_CREDIT;
    };
    mThis.isPickup = function () {
      return mSource.type == LIT_ITEM_PICKUP;
    };
    mThis.isSale = function () {
      return mSource.type == LIT_ITEM_SALE;
    };
    mThis.isSaleOrder = function () {
      return mThis.isSale() || mThis.isOrder();
    };

    /***************************************************************************
     * Methods
     ***************************************************************************/
    /***************************************************************************
     * applyDiscount */
    /**
     * Applies the best discount available to the entity. If the item has a
     * membership discount, and there is a membership item on the current receipt,
     * this discount may be applied, regardless of the entity.
     *
     * @param  entity               The entity on the receipt to receive the
     *                              discount.
     * @param  membershipOnReceipt  There is a membership item already on the
     *                              receipt to be purchased.
     ***************************************************************************/
    mThis.applyDiscount = function (entity, membershipOnReceipt) {
      /*------------------------------------------------------*/
      /* Do nothing if there is no product discounts          */
      /* available or the item is not meant to have discounts */
      /*------------------------------------------------------*/
      if (
        mThis.isDonation() ||
        mThis.isPickup() ||
        !mThis.productSource ||
        !mThis.productSource.itemDiscounts ||
        !mThis.productSource.itemDiscounts.length
      )
        return;

      const subItemsPriceChange = mThis.subItems.reduce(
        (acc, subItem) => acc + priceChange(subItem),
        0
      );

      /**
       * In order to get the original base price of the item before any modifications we must use the price instead of amount. This is because the amount
       * will include the tax while price will not. However also the price will include the price change and the sub item price change so we must subtract
       * those values to get the original base price of the item.
       */
      const basePrice = mThis.price - priceChange() - subItemsPriceChange;

      /*------------------------------------------------*/
      /* No discount will be added for non-fixed priced */
      /* products that have had their prices changed    */
      /*------------------------------------------------*/
      if (
        !mThis.productSource.fixedPrice &&
        basePrice != mThis.productSource.unitPrice
      )
        return;

      /*--------------*/
      /* Max discount */
      /*--------------*/
      var maxDiscount = null;

      /*-------------------------------------------------------------*/
      /* If there is a membership purchased for the current          */
      /* receipt, apply the membership discount if the item has one. */
      /*-------------------------------------------------------------*/
      if (membershipOnReceipt)
        for (var i = 0; i < mThis.productSource.itemDiscounts.length; i++)
          if (mThis.productSource.itemDiscounts[i].type == DT_MEMBERSHIP) {
            maxDiscount = mThis.productSource.itemDiscounts[i];
            break;
          }

      /*-----------------------------------------------------*/
      /* Look for the best discount available to the entity. */
      /*-----------------------------------------------------*/
      if (entity)
        mThis.productSource.itemDiscounts.forEach(function (itemDiscount) {
          if (
            (entity.types[0] == ET_STUDENT &&
              itemDiscount.type == DT_STUDENT) ||
            (entity.types[0] == ET_TEACHER &&
              itemDiscount.type == DT_TEACHER) ||
            (entity.isMember && itemDiscount.type == DT_MEMBERSHIP)
          )
            if (!maxDiscount || itemDiscount.price < (maxDiscount || {}).price)
              maxDiscount = itemDiscount;
        });

      /*-----------------------------------------------------------------*/
      /* For a group, distribute the discount amount across the subItems */
      /*-----------------------------------------------------------------*/
      if (mThis.isGroup()) {
        if (maxDiscount) {
          /*-----------------------------------------------------------------*/
          /* Build the specs from the child items. Create the source object  */
          /* for the specs with the child and corresponding product subitem. */
          /*-----------------------------------------------------------------*/
          var specs = mThis.childDGItems().map(function (dgSub) {
            /**
             * We must use the source unit price of the sub item because the price of the subitem has been manipulated. We also can not use the sourceProduct of the sub item
             * because that will not have the price changes from the kit itself.
             */
            return new DistributionSpec(
              { item: dgSub, product: dgSub.productSource },
              dgSub.productSource.unitPrice,
              0
            );
          });

          /*-----------------------------------------------------------------*/
          /* Distribute the single item discount price amongst the sub items */
          /*-----------------------------------------------------------------*/
          DistributionSpec.distribute(
            specs,
            maxDiscount.price,
            1,
            function (src, amt) {
              /*---------------------------------------*/
              /* Create a discount for the distributed */
              /* amount. The type does not matter.     */
              /*---------------------------------------*/
              src.item.setDiscount(
                Object.assign({}, maxDiscount, { price: amt })
              );
            }
          );
        } else {
          /*-------------------------------*/
          /* Otherwise, reset the subitems */
          /*-------------------------------*/
          mThis.childDGItems().forEach(function (dgSub) {
            dgSub.setDiscount();
          });
        }
      }

      /*-------------------------*/
      /* Set discount properties */
      /*-------------------------*/
      mThis.setDiscount(maxDiscount);
    };

    /***************************************************************************
     * equals */
    /**
     * Compares two DGReceiptItem objects.
     *
     * @param  dgItem  DGReceiptItem to compare against.
     * @return         Boolean indication of equality
     ***************************************************************************/
    mThis.equals = function (dgItem) {
      /** Utility function to compare numeric values. Defaults to 0 when NaN/undefined. */
      var numsEqual = function (num1, num2) {
        return (num1 || 0) == (num2 || 0);
      };

      return (
        numsEqual(mSource.type, dgItem.source().type) &&
        numsEqual(mSource.subType, dgItem.source().subType) &&
        numsEqual(mSource.referenceID, dgItem.source().referenceID) &&
        numsEqual(mSource.referenceItemID, dgItem.source().referenceItemID) &&
        numsEqual(mSource.entityID, dgItem.source().entityID) &&
        numsEqual(mSource.itemID, dgItem.source().itemID) &&
        numsEqual(mSource.accountID, dgItem.source().accountID) &&
        numsEqual(mSource.classID, dgItem.source().classID) &&
        mThis.options().length == dgItem.options().length &&
        mThis.childDGItems().length == dgItem.childDGItems().length &&
        /*-----------------------------------------------------*/
        /* This check is only for unpaid items. If this item does */
        /* not have a source then its not an unpaid item and we */
        /* short circuit the check.                            */
        /* If there is a source, check that the fineIDs match. */
        /*-----------------------------------------------------*/
        (!mSource.source ||
          (mSource.source &&
            dgItem.source().source &&
            numsEqual(mSource.source.fineID, dgItem.source().source.fineID))) &&
        /*--------------------------------------------------------------------------------------------*/
        /* For items that utilize a price indicated by not being an amount only item, the price needs */
        /* to be checked as non-fixed prices could differ between items. For fixed price items we use */
        /* unit price to compare before discounts are applied. For non-fixed price items we use       */
        /* getOriginalUnitPrice() which recovers the pre-discount price by adding back the per-unit   */
        /* discount, since the grid item may already have a discount applied while the new item does  */
        /* not. This only needs to be done for new receipts since this has already been done for      */
        /* receipts that exist. Checking this for existing receipts can cause errors when the item on */
        /* the receipt is inactive because mThis.productSource is an array of active items only -     */
        /* these are the items that can be sold. See GR-2362 for details on this error.               */
        /*--------------------------------------------------------------------------------------------*/
        (!mThis.isNew() ||
          mThis.isAmountOnly() ||
          (mThis.productSource.fixedPrice
            ? numsEqual(
                mThis.productSource.unitPrice,
                dgItem.productSource.unitPrice
              )
            : numsEqual(
                mThis.getOriginalUnitPrice(),
                dgItem.getOriginalUnitPrice()
              ))) &&
        /*-------------------------------------*/
        /* Verify same item options by looking */
        /* for non-equavalent item options     */
        /*-------------------------------------*/
        !mThis.options().some(function (lio) {
          var optionMatch = dgItem
            .options()
            .filter(function (lio2) {
              return lio.itemOptionID == lio2.itemOptionID;
            })
            .shift();

          return !optionMatch || lio.optionValue != optionMatch.optionValue;
        }) &&
        /*--------------------------------------------------------*/
        /* Verify equivalent sub items by looking for non-matches */
        /*--------------------------------------------------------*/
        !mThis.childDGItems().some(function (si) {
          var subMatch = dgItem
            .childDGItems()
            .filter(function (si2) {
              return si.itemID() == si2.itemID();
            })
            .shift();

          return !subMatch || !si.equals(subMatch);
        })
      );
    };

    /***************************************************************************
     * getTax */
    /**
     * Returns the tax amount calculation for the source ReceiptItem. If the
     * source is a group, the tax is the total of the subItem tax amounts.
     *
     * @return  Numeric tax amount
     ***************************************************************************/
    mThis.getTax = function () {
      var tax = 0;

      if (mThis.isGroup())
        mThis.childDGItems().forEach(function (dgSub) {
          tax += dgSub.getTax();
        });
      else
        tax = Tools.roundToPrecision(
          mSource.subTotal * (mSource.taxable ? mSource.taxRate : 0),
          2
        );

      return tax;
    };

    /***************************************************************************
     * increase */
    /**
     * Increases either the quantity or the amount, depending on if this is an
     * amount only receipt item.
     *
     * @param  dgItem  DGItem to update this instance from
     ***************************************************************************/
    mThis.increase = function (dgItem) {
      if (this.isAmountOnly())
        mSource.amount = Tools.roundToPrecision(
          mSource.amount + dgItem.amount,
          2
        );
      else mThis.quantity = mThis.quantity + dgItem.quantity;
    };

    /***************************************************************************
     * priceChange */
    /**
     * Returns the total price changes from the list of LedgerItemOptions.
     *
     * @returns {Number}
     ***************************************************************************/
    function priceChange(subItem) {
      var change = 0;

      /**
       * This or check and receiptItem parameter was added for sub items of groups. Because we needed
       * a way to use this function to get the price change of the sub items of a group and the base
       * item itself since not every item we add to the cart will be a group item.
       *
       * If the receiptItem is not provided then we will use the source item which is just the base item.
       *
       * When the receiptItem is provided it takes priority over the source item. If its not provided we need
       * to make sure we check if it exists otherwise we will get an error when trying to access its options.
       */
      const optionsToCheck = (subItem && subItem.options()) || mSource.options;

      optionsToCheck.forEach(function (option) {
        change += Number(option.priceChange) || 0;
      });
      return change;
    }

    /***************************************************************************
     * recalc */
    /**
     * Updates the subtotal, tax, and amount properties when appropriate from
     * the quantity and price.
     ***************************************************************************/
    function recalc() {
      if (mThis.isAmountOnly()) return;

      /*-----------------------------------------------------*/
      /* Update the price from subItems when this is a group */
      /*-----------------------------------------------------*/
      if (mThis.isGroup()) {
        mSource.price = 0;
        mSource.subItems.forEach(function (si) {
          mSource.price = Tools.roundToPrecision(mSource.price + si.price, 2);
        });
      }

      /*------------------*/
      /* Set the subtotal */
      /*------------------*/
      mSource.subTotal = Tools.roundToPrecision(
        mSource.quantity * mSource.price,
        2
      );

      /*-----------------------*/
      /* Update the tax amount */
      /*-----------------------*/
      mSource.tax = Tools.roundToPrecision(mThis.getTax(), 2);

      /*-------------------*/
      /* Update the amount */
      /*-------------------*/
      mSource.amount = Tools.roundToPrecision(
        (mSource.subTotal || 0) + (mSource.tax || 0),
        2
      );
    }

    /***************************************************************************
     * setDiscount */
    /**
     * Set the discount on the object. If no discount is provided, use the source
     * product amount. Ensure the priceChange amount from the options are included
     * and then update the totals.
     *
     * @param discount ItemDiscount object to set
     ***************************************************************************/
    mThis.setDiscount = function (discount) {
      mSource.price =
        (discount ? discount.price : mThis.productSource.amount) +
        priceChange();
      mSource.discount = discount
        ? Tools.roundToPrecision(
            (mThis.productSource.amount - discount.price) * mSource.quantity,
            2
          )
        : 0;
      mSource.discountAcctID = discount ? discount.accountID : 0;

      recalc();
    };

    /***************************************************************************
     * tooltip */
    /**
     * Builds the tool tip from the selected options.
     ***************************************************************************/
    mThis.tooltip = function () {
      var s = '';

      mSource.options.forEach(function (option) {
        s +=
          (s.length > 0 ? '\n' : '') +
          option.optionMasterName +
          ': ' +
          option.optionValue;
      });

      return s;
    };

    /*-------------------------------------------*/
    /* Set the source id property to the next    */
    /* temporary ID that is available if not set */
    /*-------------------------------------------*/
    if (!mSource.id) mSource.id = --mTempID;

    /*--------------------------------------------------------------*/
    /* If the source has subItems, map them to child DGReceiptItems */
    /* and update the subItems property of this object              */
    /*--------------------------------------------------------------*/
    mThis.subItems = (mSource.subItems || []).map(function (si) {
      return new Data.DGReceiptItem(si, true);
    });

    /*-----------------------------*/
    /* Ensure total values are set */
    /*-----------------------------*/
    recalc();
  };

  /*****************************************************************************
   * additionalDescription */
  /**
   * Returns a string to append to the description being built to display in the
   * memo column. This is defined on the prototype so it can be overriden.
   *****************************************************************************/
  Data.DGReceiptItem.prototype.additionalDescription = function () {
    return this.descriptionClass() + this.descriptionName();
  };
}
